import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useCreateBundleComponent, useSetBundleDepartureDates } from '~/hooks/apis/bundle/useBundles';

import { BUNDLE_OCCUPANCIES, Bundle, TourBundleComponent } from '~/types/bundles/bundle';
import { TourOfferSummary } from '~/types/tours/tourOffer';

import BaseTourPicker from './BaseTourPicker';
import DepartureSelector from './DepartureSelector';
import TourOfferName from './TourOfferName';

interface Props {
  bundle: Bundle;
  /** Set to edit the departures of the tour already in the bundle. */
  component?: TourBundleComponent;
  open: boolean;
  onClose: () => void;
}

const DAY_OFFSET_PATTERN = /^\d+$/;

export default function TourComponentDialog({ bundle, component, open, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [tourOffer, setTourOffer] = useState<TourOfferSummary | null>(null);
  const [dayOffset, setDayOffset] = useState('0');
  const [selected, setSelected] = useState<Array<string>>([]);

  const createComponent = useCreateBundleComponent(bundle.id);
  const setDepartureDates = useSetBundleDepartureDates(bundle.id);

  const isEdit = !!component;
  const tourOfferId = component?.tour_offer_id ?? tourOffer?.id;
  const isValidDayOffset = DAY_OFFSET_PATTERN.test(dayOffset);
  const error = createComponent.error ?? setDepartureDates.error;
  const isSaving = createComponent.isPending || setDepartureDates.isPending;

  useEffect(() => {
    if (open) {
      setSelected(isEdit ? bundle.departure_dates : []);
    }
  }, [open, isEdit, bundle.departure_dates]);

  const handleTourChange = (offer: TourOfferSummary | null) => {
    setTourOffer(offer);
    setSelected([]);
  };

  const handleClose = () => {
    setTourOffer(null);
    setDayOffset('0');
    setSelected([]);
    createComponent.reset();
    setDepartureDates.reset();
    onClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      // A retry after the dates failed must not add the tour a second time.
      const hasTour = bundle.components.some((c) => c.product_type === 'tour');
      if (!isEdit && !hasTour) {
        await createComponent.mutateAsync({
          product_type: 'tour',
          sequence_number: Math.max(-1, ...bundle.components.map((c) => c.sequence_number)) + 1,
          day_offset: Number(dayOffset),
          tour_offer_id: tourOffer.id,
          occupancies: [...BUNDLE_OCCUPANCIES],
        });
      }
      await setDepartureDates.mutateAsync([...selected].sort());
      enqueueSnackbar(isEdit ? 'Departures saved' : 'Tour added', { variant: 'success' });
      handleClose();
    } catch {
      // The mutation error is shown in the dialog.
    }
  };

  const departureCount = `${selected.length} departure${selected.length === 1 ? '' : 's'}`;
  const submitLabel = isEdit ? 'Save departures' : selected.length > 0 ? `Add tour with ${departureCount}` : 'Add tour';

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <form onSubmit={handleSubmit}>
        <DialogTitle>{isEdit ? 'Edit departures' : 'Add tour'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            <DialogContentText>
              {isEdit
                ? 'Choose which of the tour’s departures this bundle sells.'
                : 'Pick the base tour this bundle’s LTLE will be made from, then choose the departures to sell.'}
            </DialogContentText>

            {isEdit ? (
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Base tour
                </Typography>
                <Typography variant="subtitle1">
                  <TourOfferName tourOfferId={component.tour_offer_id} />
                </Typography>
              </Stack>
            ) : (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                <BaseTourPicker value={tourOffer} onChange={handleTourChange} sx={{ flex: 1 }} />
                <TextField
                  label="Starts on day"
                  type="number"
                  value={dayOffset}
                  onChange={(event) => setDayOffset(event.target.value)}
                  error={!isValidDayOffset}
                  helperText="0 is the departure day"
                  inputProps={{ min: 0, step: 1 }}
                  sx={{ width: { xs: '100%', sm: 160 } }}
                  required
                />
              </Stack>
            )}

            {!!tourOfferId && (
              <DepartureSelector
                tourOfferId={tourOfferId}
                selected={selected}
                onChange={setSelected}
                savedDates={isEdit ? bundle.departure_dates : undefined}
                disabled={isSaving}
              />
            )}

            {!!error && <Alert severity="error">{error.message}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!tourOfferId || !isValidDayOffset || selected.length === 0 || isSaving}
          >
            {submitLabel}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
